import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@context/AuthContext';
import { trpc } from '@/api/trpc';
import Dropdown from '@components/UI/Dropdown';
import Badge from '@components/UI/Badge';
import { FaUserCircle } from 'react-icons/fa';

interface HeaderProps {
    onSearchClick: () => void;
}

const Header = ({ onSearchClick }: HeaderProps) => {
    const { user, isAuthenticated, isImpersonating, logout, sessionPolicy } = useAuth();
    const [isNavOpen, setIsNavOpen] = useState(false);
    const location = useLocation();

    const activeRunsQuery = useQuery({
        ...trpc.agent.countActiveRuns.queryOptions(),
        enabled: isAuthenticated && sessionPolicy.canUseAgentChat,
        staleTime: 30_000,
        refetchInterval: (activeRunsQuery) => ((activeRunsQuery.state.data?.count ?? 0) > 0 ? 10_000 : 60_000),
        retry: false
    });

    const activeRunCount = activeRunsQuery.data?.count ?? 0;

    const redirectUrl = encodeURIComponent(location.pathname);

    return (
        // Establish a stacking context above page content (sticky headers use z-20) but below modals
        // and chart focus mode (z-40/z-50) so the nav dropdown menus always render over the page.
        <header className="relative z-30">
            <nav className="bg-[#161616] text-white">
                <div className="w-full px-0 py-3">
                    <div className="flex flex-wrap items-center justify-between px-4">
                        {/* Left: Brand */}
                        <Link className="text-xl font-semibold text-white" to="/">
                            Project 314
                        </Link>

                        {/* Toggler */}
                        <button
                            aria-controls="navbarSupportedContent"
                            aria-expanded={isNavOpen}
                            aria-label="Toggle navigation"
                            className="lg:hidden rounded border border-zinc-600 px-2 py-1"
                            type="button"
                            onClick={() => setIsNavOpen(!isNavOpen)}
                        >
                            <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z" />
                            </svg>
                        </button>

                        {/* Right: Collapsible Content */}
                        <div
                            className={`${isNavOpen ? 'block' : 'hidden'} w-full lg:flex lg:w-auto lg:items-center`}
                            id="navbarSupportedContent"
                        >
                            <ul className="flex flex-col lg:ml-auto lg:flex-row lg:items-center">
                                <li className="mr-3">
                                    <button
                                        aria-label="Search"
                                        className="text-zinc-300 hover:text-white p-2"
                                        id="searchIcon"
                                        type="button"
                                        onClick={onSearchClick}
                                    >
                                        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="M10.442 10.442a1 1 0 0 1 1.415 0l3.85 3.85a1 1 0 0 1-1.414 1.415l-3.85-3.85a1 1 0 0 1 0-1.415z"
                                                fillRule="evenodd"
                                            />
                                            <path
                                                d="M6.5 12a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11zM13 6.5a6.5 6.5 0 1 1-13 0 6.5 6.5 0 0 1 13 0z"
                                                fillRule="evenodd"
                                            />
                                        </svg>
                                    </button>
                                </li>

                                {!isAuthenticated ? (
                                    <>
                                        <li className="py-2 lg:py-0">
                                            <Link
                                                className="block px-4 py-2 text-sm font-medium text-zinc-300 hover:text-white"
                                                to={`/login?redirect=${redirectUrl}`}
                                            >
                                                Login
                                            </Link>
                                        </li>
                                        <li className="py-2 lg:py-0">
                                            <Link
                                                className="block px-4 py-2 text-sm font-medium text-zinc-300 hover:text-white"
                                                to="/register"
                                            >
                                                Register
                                            </Link>
                                        </li>
                                    </>
                                ) : (
                                    <>
                                        <li className="py-2 lg:py-0">
                                            <Dropdown buttonContent="Backtests">
                                                <div>
                                                    <Link
                                                        className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                        to="/user/portfolioBacktest"
                                                    >
                                                        Portfolio Backtests
                                                    </Link>
                                                </div>
                                                {sessionPolicy.canAccessAdminRoutes && (
                                                    <div>
                                                        <Link
                                                            className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                            to="/tools/backtestTrades"
                                                        >
                                                            Backtest Trades
                                                        </Link>
                                                    </div>
                                                )}
                                                {sessionPolicy.canAccessAdminRoutes && (
                                                    <>
                                                        <div>
                                                            <Link
                                                                className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                                to="/tools/filterBacktests"
                                                            >
                                                                Filter Backtests
                                                            </Link>
                                                        </div>
                                                        <div>
                                                            <Link
                                                                className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                                to="/tools/screeners"
                                                            >
                                                                Screeners
                                                            </Link>
                                                        </div>
                                                        <div>
                                                            <Link
                                                                className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                                to="/tools/chartPatternAudit"
                                                            >
                                                                Chart Pattern Audit
                                                            </Link>
                                                        </div>
                                                        <div>
                                                            <Link
                                                                className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                                to="/tools/elliottWaveAudit"
                                                            >
                                                                Elliott Wave Audit
                                                            </Link>
                                                        </div>
                                                        <div>
                                                            <Link
                                                                className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                                to="/tools/vpaAudit"
                                                            >
                                                                VPA Audit
                                                            </Link>
                                                        </div>
                                                        <div>
                                                            <Link
                                                                className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                                to="/tools/obvAudit"
                                                            >
                                                                OBV Audit
                                                            </Link>
                                                        </div>
                                                        <div>
                                                            <Link
                                                                className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                                to="/tools/alphaAtlas"
                                                            >
                                                                Alpha Atlas Run Health
                                                            </Link>
                                                        </div>
                                                        <div>
                                                            <Link
                                                                className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                                to="/tools/intelCallReview"
                                                            >
                                                                X Call Review
                                                            </Link>
                                                        </div>
                                                    </>
                                                )}
                                            </Dropdown>
                                        </li>
                                        <li className="py-2 lg:py-0">
                                            <Dropdown buttonContent="Entries">
                                                <div>
                                                    <Link
                                                        className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                        to="/user/entryLog"
                                                    >
                                                        Entry Log
                                                    </Link>
                                                </div>
                                                <div>
                                                    <Link
                                                        className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                        to="/user/realTimeEntries"
                                                    >
                                                        Real Time Entries
                                                    </Link>
                                                </div>
                                                <div>
                                                    <Link
                                                        className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                        to="/user/tradeLogicExecutionJournals"
                                                    >
                                                        Trade Logic Activity
                                                    </Link>
                                                </div>
                                            </Dropdown>
                                        </li>
                                        <li className="py-2 lg:py-0">
                                            <Dropdown buttonContent="Strategies">
                                                <div>
                                                    <Link
                                                        className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                        to="/user/tradingStrategies"
                                                    >
                                                        Trading Strategies
                                                    </Link>
                                                </div>
                                                <div>
                                                    <Link
                                                        className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                        to="/user/strategyLeaderboard"
                                                    >
                                                        Strategy Leaderboard
                                                    </Link>
                                                </div>
                                                <div>
                                                    <Link
                                                        className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                        to="/user/tradeLogic"
                                                    >
                                                        Trading Rules
                                                    </Link>
                                                </div>
                                                <div>
                                                    <Link
                                                        className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                        to="/user/aero"
                                                    >
                                                        AERO
                                                    </Link>
                                                </div>
                                                <div>
                                                    <Link
                                                        className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                        to="/user/exitCandidates"
                                                    >
                                                        Global Exit Candidates
                                                    </Link>
                                                </div>
                                                <div>
                                                    <Link
                                                        className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                        to="/user/entryOptimizer"
                                                    >
                                                        Entry Optimizer
                                                    </Link>
                                                </div>
                                                <div>
                                                    <Link
                                                        className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                        to="/user/ideaLab"
                                                    >
                                                        Idea Lab
                                                    </Link>
                                                </div>
                                                <div>
                                                    <Link
                                                        className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                        to="/user/optionBuyingStrategies"
                                                    >
                                                        Option Buying Strategies
                                                    </Link>
                                                </div>
                                                <div>
                                                    <Link
                                                        className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                        to="/user/scorecards"
                                                    >
                                                        Scorecards
                                                    </Link>
                                                </div>
                                                <div>
                                                    <Link
                                                        className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                        to="/user/alphaAtlas"
                                                    >
                                                        Alpha Report
                                                    </Link>
                                                </div>
                                                <div>
                                                    <Link
                                                        className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                        to="/user/tradingProfiles"
                                                    >
                                                        Trading Profiles
                                                    </Link>
                                                </div>
                                                <div>
                                                    <Link
                                                        className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                        to="/user/entityLists"
                                                    >
                                                        Entity Lists
                                                    </Link>
                                                </div>
                                            </Dropdown>
                                        </li>
                                        <li className="py-2 lg:py-0">
                                            <Dropdown buttonContent="Experiments" position="right">
                                                <div>
                                                    <Link
                                                        className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                        to="/user/experiments"
                                                    >
                                                        Experiments
                                                    </Link>
                                                </div>
                                                <div>
                                                    <Link
                                                        className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                        to="/user/experimentCompare"
                                                    >
                                                        Compare Results
                                                    </Link>
                                                </div>
                                            </Dropdown>
                                        </li>
                                        {sessionPolicy.canUseAgentChat && (
                                            <li className="py-2 lg:py-0">
                                                <Link
                                                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-zinc-300 hover:text-white"
                                                    to="/user/chat"
                                                >
                                                    AI Chat
                                                    {activeRunCount > 0 && <Badge pill text={activeRunCount} variant="success" />}
                                                </Link>
                                            </li>
                                        )}
                                        <li className="py-2 lg:py-0">
                                            <Dropdown buttonContent={<FaUserCircle className="h-6 w-6" />} position="right">
                                                <div>
                                                    <Link
                                                        className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                        to="/user/portfolios"
                                                    >
                                                        Portfolios
                                                    </Link>
                                                </div>
                                                <div>
                                                    <Link
                                                        className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                        to="/user/strategySubscriptions"
                                                    >
                                                        Strategy Subscriptions
                                                    </Link>
                                                </div>
                                                {sessionPolicy.canManageBrokerConnections && (
                                                    <div>
                                                        <Link
                                                            className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                            to="/user/tradingAccounts"
                                                        >
                                                            Trading Accounts
                                                        </Link>
                                                    </div>
                                                )}
                                                {sessionPolicy.canManageBrokerConnections && (
                                                    <div>
                                                        <Link
                                                            className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                            to="/user/thirdPartyConnections"
                                                        >
                                                            Third Party Connections
                                                        </Link>
                                                    </div>
                                                )}
                                                {sessionPolicy.canManageMcpAccessTokens && (
                                                    <div>
                                                        <Link
                                                            className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                            to="/user/developerAccess"
                                                        >
                                                            Developer Access
                                                        </Link>
                                                    </div>
                                                )}
                                                {sessionPolicy.canAccessAdminRoutes && (
                                                    <div>
                                                        <Link
                                                            className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                            to="/user/aiApprovals"
                                                        >
                                                            AI Approvals
                                                        </Link>
                                                    </div>
                                                )}
                                                {sessionPolicy.canManage2fa && !user?.['2faEnabled'] && (
                                                    <div>
                                                        <Link
                                                            className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                            to="/user/enable2fa"
                                                        >
                                                            Enable 2FA
                                                        </Link>
                                                    </div>
                                                )}
                                                {sessionPolicy.canAccessAdminRoutes && (
                                                    <div className="border-t border-zinc-600 mt-2 pt-2">
                                                        <Link
                                                            className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                            to="/tools/supportSessions"
                                                        >
                                                            Support Sessions
                                                        </Link>
                                                    </div>
                                                )}
                                                {sessionPolicy.supportSessionEnabled && !isImpersonating && (
                                                    <div className="border-t border-zinc-600 mt-2 pt-2">
                                                        <Link
                                                            className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                            to="/tools/supportSessions?startSession=1"
                                                        >
                                                            Support Access
                                                        </Link>
                                                    </div>
                                                )}
                                                <div className="border-t border-zinc-600 mt-2 pt-2">
                                                    <Link
                                                        className="block w-full px-4 py-2 text-left text-white hover:bg-[#252525]"
                                                        to="/logout"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            void logout();
                                                        }}
                                                    >
                                                        Logout
                                                    </Link>
                                                </div>
                                            </Dropdown>
                                        </li>
                                    </>
                                )}
                            </ul>
                        </div>
                    </div>
                </div>
            </nav>
        </header>
    );
};

export default Header;
